/**
 * useSeasonalityModeData
 *
 * Every derived map and getter the Monthly Seasonality view needs: positional seasonality-year
 * data, stored-calculation lookups, actual-data lookups (comparison mode), seasonality weight
 * getters and the bottom-up expense/income computations. Extracted verbatim from
 * SeasonalityModeView so the department section, aggregates and export builder share one
 * implementation. The getters are plain closures (not memoized), as they were in the view.
 */

import { useMemo } from 'react';
import { MonthlyTotals, createEmptyMonthlyTotals, addMonthlyTotals } from '../../types';
import { DOC_TEMPLATE, ExpenseCategory, resolveAccountsForDepartment } from '../../config/docTemplate';
import { SeasonalityModeViewProps } from './seasonalityModeTypes';
import { buildTotalGrossMap, buildExpenseMap, buildAccountMap } from './seasonalityModeHelpers';
import {
  applySeasonality,
  HistoricalYearData,
  calculateSeasonalityWeights as calculateSeasonalityWeightsUtil,
} from '../../utils/seasonalityCalculation';

export type SeasonalityModeDataInputs = Pick<SeasonalityModeViewProps,
  | 'inputs' | 'calculations' | 'localInputs' | 'historicalYears' | 'seasonalityYears'
  | 'historicalDataByYear' | 'actualData' | 'comparisonMode' | 'isConsolidated' | 'expensesByAccount'
  | 'getProjectionValue' | 'getTotalProjectedUnits' | 'getCalculatedGross'
  | 'getCategoryAccountExpenses' | 'getServiceDeptProjections'
>;

// Local wrapper for calculateSeasonalityWeights that takes individual year data.
// POSITIONAL: the *2023/*2024/*2025 names throughout this component mean the
// oldest/middle/newest seasonality year (see seasonalityYears), not literal years.
// The util weights by position (newest first), so the `year` labels are cosmetic.
function calculateSeasonalityWeights(
  monthly2023: MonthlyTotals | undefined,
  monthly2024: MonthlyTotals | undefined,
  monthly2025: MonthlyTotals | undefined
): number[] {
  // Build historical years array in order (most recent first)
  const historicalYears: HistoricalYearData[] = [];

  if (monthly2025) {
    historicalYears.push({ year: 3, monthlyTotals: monthly2025 });
  }
  if (monthly2024) {
    historicalYears.push({ year: 2, monthlyTotals: monthly2024 });
  }
  if (monthly2023) {
    historicalYears.push({ year: 1, monthlyTotals: monthly2023 });
  }

  const { percentages } = calculateSeasonalityWeightsUtil(historicalYears);
  return percentages;
}

export function useSeasonalityModeData({
  inputs,
  calculations,
  localInputs,
  historicalYears,
  seasonalityYears,
  historicalDataByYear,
  actualData,
  comparisonMode,
  isConsolidated = false,
  expensesByAccount,
  getProjectionValue,
  getTotalProjectedUnits,
  getCalculatedGross,
  getCategoryAccountExpenses,
  getServiceDeptProjections,
}: SeasonalityModeDataInputs) {
  // Seasonality-year data, POSITIONAL: 2025 = newest, 2024 = middle, 2023 = oldest
  const historical2025 = historicalDataByYear[seasonalityYears[0]] || null;
  const historical2024 = historicalDataByYear[seasonalityYears[1]] || null;
  const historical2023 = historicalDataByYear[seasonalityYears[2]] || null;

  // Display-only years (e.g. the partial 2026 during the 2027 window): never used
  // for weights, but their accounts still count as "has data" so an expense that
  // first appears in that year is included in totals (and matches the Historical view)
  const extraDisplayYearKey = historicalYears.filter(y => !seasonalityYears.includes(y)).join(',');
  const extraDisplayAccountCodes = useMemo(() => {
    const codes = new Set<string>();
    extraDisplayYearKey.split(',').filter(Boolean).forEach(y => {
      historicalDataByYear[Number(y)]?.accounts?.forEach(acc => codes.add(acc.account));
    });
    return codes;
  }, [extraDisplayYearKey, historicalDataByYear]);

  // Account codes that carry a projection input (EXPENSE_xxx / INCOME_xxx), saved OR
  // entered in this session - so what the Historical view shows is what gets saved
  const inputAccountCodes = useMemo(() => {
    const codes = new Set<string>();
    const addMetric = (metric: string) => {
      const m = metric.match(/^(?:EXPENSE|INCOME)_(.+)$/);
      if (m && !m[1].endsWith('_PER_UNIT')) codes.add(m[1]);
    };
    inputs.forEach(i => addMetric(i.metric));
    Object.keys(localInputs).forEach(key => addMetric(key.split('#')[1] || ''));
    return codes;
  }, [inputs, localInputs]);

  // Build a map of stored calculations for quick lookup
  // Key: "DEPARTMENT#METRIC" -> monthlyDistribution
  const calculationsMap = useMemo(() => {
    const map = new Map<string, MonthlyTotals>();
    if (calculations) {
      for (const calc of calculations) {
        const key = `${calc.department}#${calc.metric}`;
        if (calc.monthlyDistribution) {
          map.set(key, calc.monthlyDistribution);
        }
      }
    }
    return map;
  }, [calculations]);

  // Same keys, the seasonality base: an Executive monthly adjustment's baseDistribution when
  // the record carries one, else the stored spread. The save payload is built from this so the
  // backend re-applies deltas on a clean base instead of doubling them.
  const calculationsBaseMap = useMemo(() => {
    const map = new Map<string, MonthlyTotals>();
    if (calculations) {
      for (const calc of calculations) {
        const key = `${calc.department}#${calc.metric}`;
        const base = calc.manualAdjustment?.baseDistribution ?? calc.monthlyDistribution;
        if (base) map.set(key, base);
      }
    }
    return map;
  }, [calculations]);

  // Build maps for actual data lookup when in comparison mode
  const actualDeptDataMap = useMemo(() => {
    const map = new Map<string, {
      gross: { monthly: MonthlyTotals; ytd: number };
      expenses: { monthly: MonthlyTotals; ytd: number } | null;
      operatingProfit: { monthly: MonthlyTotals; ytd: number } | null;
    }>();
    if (actualData?.docGross) {
      for (const dept of actualData.docGross) {
        const grossMonthly = dept.gross || dept.monthlyGross || createEmptyMonthlyTotals();
        const expensesMonthly = dept.totalExpenses || null;
        const profitMonthly = dept.operatingProfit || null;
        map.set(dept.department, {
          gross: { monthly: grossMonthly, ytd: dept.ytdGross },
          expenses: expensesMonthly ? { monthly: expensesMonthly, ytd: dept.ytdTotalExpenses || 0 } : null,
          operatingProfit: profitMonthly ? { monthly: profitMonthly, ytd: dept.ytdOperatingProfit || 0 } : null,
        });
      }
    }
    return map;
  }, [actualData]);

  const actualAccountMap = useMemo(() => {
    const map = new Map<string, { monthly: MonthlyTotals; ytd: number }>();
    if (actualData?.accounts) {
      for (const acc of actualData.accounts) {
        map.set(acc.account, { monthly: acc.monthlyTotals, ytd: acc.ytdTotal });
      }
    }
    return map;
  }, [actualData]);

  const actualUnitCounts = actualData?.unitCountsData || null;

  // Helper to get actual data for a department
  // For vehicle departments, uses pre-calculated totalGross from data loader (front-end + F&I)
  // Falls back to calculating if totalGross not available (for backwards compatibility)
  const getActualDeptGross = (deptName: string): { monthly: MonthlyTotals; ytd: number } | null => {
    // Find the department data in the raw docGross array
    const deptDocGross = actualData?.docGross?.find(d => d.department === deptName);

    // Use pre-calculated totalGross if available (from updated data loader)
    if (deptDocGross?.totalGross && deptDocGross?.ytdTotalGross !== undefined) {
      return {
        monthly: deptDocGross.totalGross,
        ytd: deptDocGross.ytdTotalGross,
      };
    }

    // Fallback: calculate by combining front-end + F&I (for data loaded before this update)
    const deptData = actualDeptDataMap.get(deptName)?.gross;
    if (!deptData) return null;

    // For vehicle departments, add the corresponding F&I gross
    if (deptName === 'NEW_VEHICLE') {
      const fiData = actualDeptDataMap.get('F_AND_I_NEW')?.gross;
      if (fiData) {
        return {
          monthly: addMonthlyTotals(deptData.monthly, fiData.monthly),
          ytd: deptData.ytd + fiData.ytd,
        };
      }
    } else if (deptName === 'USED_VEHICLE') {
      const fiData = actualDeptDataMap.get('F_AND_I_USED')?.gross;
      if (fiData) {
        return {
          monthly: addMonthlyTotals(deptData.monthly, fiData.monthly),
          ytd: deptData.ytd + fiData.ytd,
        };
      }
    }

    return deptData;
  };

  const getActualDeptExpenses = (deptName: string): { monthly: MonthlyTotals; ytd: number } | null => {
    return actualDeptDataMap.get(deptName)?.expenses || null;
  };

  // Helper to get actual front-end gross (without F&I) for vehicle departments
  const getActualFrontGross = (deptName: string): { monthly: MonthlyTotals; ytd: number } | null => {
    return actualDeptDataMap.get(deptName)?.gross || null;
  };

  // Helper to get actual F&I gross for vehicle departments
  const getActualFIGross = (deptName: string): { monthly: MonthlyTotals; ytd: number } | null => {
    if (deptName === 'NEW_VEHICLE') {
      return actualDeptDataMap.get('F_AND_I_NEW')?.gross || null;
    } else if (deptName === 'USED_VEHICLE') {
      return actualDeptDataMap.get('F_AND_I_USED')?.gross || null;
    }
    return null;
  };

  // Helper to get actual sales for service departments (SERVICE, PARTS, BODY_SHOP)
  const getActualDeptSales = (deptName: string): { monthly: MonthlyTotals; ytd: number } | null => {
    const deptDocGross = actualData?.docGross?.find(d => d.department === deptName);
    if (deptDocGross?.sales && deptDocGross?.ytdSales !== undefined) {
      return {
        monthly: deptDocGross.sales,
        ytd: deptDocGross.ytdSales,
      };
    }
    return null;
  };

  const getActualDeptProfit = (deptName: string): { monthly: MonthlyTotals; ytd: number } | null => {
    return actualDeptDataMap.get(deptName)?.operatingProfit || null;
  };

  // Helper to get service pay-type gross breakdown from actual data
  const getServicePayTypeBreakdown = (deptName: string) => {
    const deptDocGross = actualData?.docGross?.find(d => d.department === deptName);
    return deptDocGross?.serviceGrossByPayType || null;
  };

  // Helper to get parts pay-type gross breakdown from actual data
  const getPartsPayTypeBreakdown = (deptName: string) => {
    const deptDocGross = actualData?.docGross?.find(d => d.department === deptName);
    return deptDocGross?.partsGrossByPayType || null;
  };

  // Helper to get actual account value
  const getActualAccountValue = (accountCode: string): { monthly: MonthlyTotals; ytd: number } | null => {
    return actualAccountMap.get(accountCode) || null;
  };

  /**
   * Get stored monthly distribution from DynamoDB calculations.
   * This ensures the Projections page displays the SAME values as the Forecast page purple line.
   * Falls back to on-the-fly calculation if not found (for backwards compatibility).
   *
   * IMPORTANT: If the stored monthly values don't sum to the expected annual total,
   * we recalculate to ensure consistency. This handles cases where the annual input
   * changed but the stored monthly distribution is stale.
   */
  const getStoredMonthlyDistribution = (
    department: string,
    metric: string,
    annualTotal: number,
    fallbackWeights: number[],
    source: 'display' | 'base' = 'display'
  ): MonthlyTotals => {
    const key = `${department}#${metric}`;
    const stored = (source === 'base' ? calculationsBaseMap : calculationsMap).get(key);
    if (stored) {
      // Verify stored values are consistent with expected annual total
      const storedSum = Object.values(stored).reduce((sum, val) => sum + (val || 0), 0);
      // Allow small rounding tolerance (within $1) on dollars; units must match exactly
      if (Math.abs(storedSum - annualTotal) <= (metric === 'UNITS' ? 0 : 1)) {
        return stored;
      }
      // Stale data detected - recalculate using seasonality weights
      console.log(`Stale monthly distribution detected for ${key}: stored sum=${storedSum}, expected=${annualTotal}. Recalculating.`);
    }
    // Calculate on-the-fly using seasonality weights
    return applySeasonality(annualTotal, fallbackWeights);
  };

  /**
   * Get stored monthly distribution for vehicle departments (NEW_VEHICLE, USED_VEHICLE).
   * Mirrors the Forecast page's get-daily-progression logic:
   * 1. Try TOTAL_GROSS first
   * 2. If not found, ADD the monthly values from GROSS + FI_INCOME
   * 3. Only fall back to on-the-fly calculation if neither exists
   *
   * IMPORTANT: If the stored monthly values don't sum to the expected annual total,
   * we recalculate to ensure consistency.
   */
  const getVehicleDeptMonthlyGross = (
    department: string,
    annualTotal: number,
    fallbackWeights: number[],
    source: 'display' | 'base' = 'display'
  ): MonthlyTotals => {
    const lookup = source === 'base' ? calculationsBaseMap : calculationsMap;
    // Helper to check if stored values match expected total
    const isConsistent = (stored: MonthlyTotals): boolean => {
      const storedSum = Object.values(stored).reduce((sum, val) => sum + (val || 0), 0);
      return Math.abs(storedSum - annualTotal) <= 1; // Allow $1 rounding tolerance
    };

    // First try TOTAL_GROSS (like Forecast page does)
    const totalGrossKey = `${department}#TOTAL_GROSS`;
    const storedTotalGross = lookup.get(totalGrossKey);
    if (storedTotalGross && isConsistent(storedTotalGross)) {
      return storedTotalGross;
    }

    // Fallback: Add GROSS + FI_INCOME monthly distributions (matches Forecast page logic)
    const grossKey = `${department}#GROSS`;
    const fiKey = `${department}#FI_INCOME`;
    const fiAltKey = `${department}#F_AND_I`; // Alternative key name

    const storedGross = lookup.get(grossKey);
    const storedFI = lookup.get(fiKey) || lookup.get(fiAltKey);

    if (storedGross || storedFI) {
      const combined = addMonthlyTotals(storedGross, storedFI);
      if (isConsistent(combined)) {
        return combined;
      }
      console.log(`Stale monthly distribution detected for ${department}: stored sum doesn't match expected=${annualTotal}. Recalculating.`);
    }

    // Calculate on-the-fly using seasonality weights
    return applySeasonality(annualTotal, fallbackWeights);
  };

  // Build historical maps using helper functions
  const grossMap2023 = useMemo(() => buildTotalGrossMap(historical2023), [historical2023]);
  const grossMap2024 = useMemo(() => buildTotalGrossMap(historical2024), [historical2024]);
  const grossMap2025 = useMemo(() => buildTotalGrossMap(historical2025), [historical2025]);

  const expenseMap2023 = useMemo(() => buildExpenseMap(historical2023), [historical2023]);
  const expenseMap2024 = useMemo(() => buildExpenseMap(historical2024), [historical2024]);
  const expenseMap2025 = useMemo(() => buildExpenseMap(historical2025), [historical2025]);

  const accountMap2023 = useMemo(() => buildAccountMap(historical2023), [historical2023]);
  const accountMap2024 = useMemo(() => buildAccountMap(historical2024), [historical2024]);
  const accountMap2025 = useMemo(() => buildAccountMap(historical2025), [historical2025]);

  // An account participates in projected totals if any display year has it
  // (seasonality years + the partial prior year) or it already carries an input.
  // Keep in sync with ProjectionInputView/DepartmentRenderer (Historical view totals).
  const hasAccountData = (accountCode: string): boolean =>
    accountMap2023.has(accountCode) || accountMap2024.has(accountCode) || accountMap2025.has(accountCode) ||
    extraDisplayAccountCodes.has(accountCode) || inputAccountCodes.has(accountCode);

  // Get seasonality weights for a department (based on gross)
  const getDeptGrossSeasonalityWeights = (dept: string): number[] => {
    return calculateSeasonalityWeights(
      grossMap2023.get(dept),
      grossMap2024.get(dept),
      grossMap2025.get(dept)
    );
  };

  // Get EXPENSE seasonality weights for a department (based on historical expenses)
  const getDeptExpenseSeasonalityWeights = (dept: string): number[] => {
    return calculateSeasonalityWeights(
      expenseMap2023.get(dept),
      expenseMap2024.get(dept),
      expenseMap2025.get(dept)
    );
  };

  // Get seasonality weights for a specific brand's units
  const getBrandUnitSeasonalityWeights = (department: string, brand: string): number[] => {
    const isNew = department === 'NEW_VEHICLE';
    const monthly2023 = isNew
      ? historical2023?.unitCountsData?.monthlyNewUnitsByBrand?.[brand]
      : historical2023?.unitCountsData?.monthlyUsedUnitsByBrand?.[brand];
    const monthly2024 = isNew
      ? historical2024?.unitCountsData?.monthlyNewUnitsByBrand?.[brand]
      : historical2024?.unitCountsData?.monthlyUsedUnitsByBrand?.[brand];
    const monthly2025 = isNew
      ? historical2025?.unitCountsData?.monthlyNewUnitsByBrand?.[brand]
      : historical2025?.unitCountsData?.monthlyUsedUnitsByBrand?.[brand];

    return calculateSeasonalityWeights(monthly2023, monthly2024, monthly2025);
  };

  // Get seasonality weights for total department units
  const getDeptUnitSeasonalityWeights = (department: string): number[] => {
    const isNew = department === 'NEW_VEHICLE';
    const monthly2023 = isNew
      ? historical2023?.unitCountsData?.monthlyNewUnits
      : historical2023?.unitCountsData?.monthlyUsedUnits;
    const monthly2024 = isNew
      ? historical2024?.unitCountsData?.monthlyNewUnits
      : historical2024?.unitCountsData?.monthlyUsedUnits;
    const monthly2025 = isNew
      ? historical2025?.unitCountsData?.monthlyNewUnits
      : historical2025?.unitCountsData?.monthlyUsedUnits;

    return calculateSeasonalityWeights(monthly2023, monthly2024, monthly2025);
  };

  // Get seasonality weights from account data
  const getAccountSeasonalityWeights = (accountCode: string): number[] => {
    return calculateSeasonalityWeights(
      accountMap2023.get(accountCode)?.monthlyTotals,
      accountMap2024.get(accountCode)?.monthlyTotals,
      accountMap2025.get(accountCode)?.monthlyTotals
    );
  };

  // Get department's TOTAL_EXPENSES from calculations (for consolidated view)
  const getDeptTotalExpensesFromCalc = (deptName: string): number => {
    if (!calculations) return 0;
    const calc = calculations.find(c => c.department === deptName && c.metric === 'TOTAL_EXPENSES');
    return calc?.annualTotal || 0;
  };

  // Get historical expense total for a department (sum of all expense accounts)
  const getHistoricalDeptExpenseTotal = (deptName: string): number => {
    const dept = DOC_TEMPLATE.find(d => d.name === deptName);
    if (!dept) return 0;

    // Resolve suffix-based pattern matching to include discovered accounts
    const allAccountCodes = Array.from(accountMap2023.keys()).concat(Array.from(accountMap2024.keys()), Array.from(accountMap2025.keys()), Array.from(actualAccountMap.keys()), Array.from(extraDisplayAccountCodes), Array.from(inputAccountCodes));
    const resolvedCats = resolveAccountsForDepartment(dept, allAccountCodes);

    let total = 0;
    resolvedCats
      .filter(cat => !['RENTAL_INCOME', 'OTHER_INCOME'].includes(cat.name))
      .forEach(cat => {
        cat.accounts.forEach(acc => {
          // Use most recent year with data
          const d2025 = accountMap2025.get(acc.code);
          const d2024 = accountMap2024.get(acc.code);
          const d2023 = accountMap2023.get(acc.code);
          const value = d2025?.ytdTotal || d2024?.ytdTotal || d2023?.ytdTotal || 0;
          total += Math.abs(value); // Expenses are typically negative, so use abs
        });
      });
    return total;
  };

  // Get historical expense amount for a single account
  const getHistoricalAccountExpense = (accCode: string): number => {
    const d2025 = accountMap2025.get(accCode);
    const d2024 = accountMap2024.get(accCode);
    const d2023 = accountMap2023.get(accCode);
    const value = d2025?.ytdTotal || d2024?.ytdTotal || d2023?.ytdTotal || 0;
    return Math.abs(value);
  };

  // Calculate monthly values for an expense account using account-level seasonality
  // Falls back to department gross seasonality if account has no history
  // For CONSOLIDATED: uses pre-calculated exact sums from expensesByAccount if available,
  // otherwise falls back to proportional estimation based on historical data
  const getAccountMonthlyExpense = (
    accCode: string,
    deptName: string,
    projectedGross: number,
    deptFallbackWeights: number[]
  ): { annual: number; monthly: MonthlyTotals } => {
    let accAmount = 0;

    if (isConsolidated) {
      // For consolidated: try to use pre-calculated exact sums first
      const expenseKey = `${deptName}#EXPENSE_${accCode}`;
      const preCalculated = expensesByAccount?.[expenseKey];

      if (preCalculated && preCalculated.annualTotal > 0) {
        // Use exact pre-calculated amount from CONSOLIDATED_SUMMARY
        const monthly: MonthlyTotals = {
          month1: preCalculated.monthlyDistribution.month1 || 0,
          month2: preCalculated.monthlyDistribution.month2 || 0,
          month3: preCalculated.monthlyDistribution.month3 || 0,
          month4: preCalculated.monthlyDistribution.month4 || 0,
          month5: preCalculated.monthlyDistribution.month5 || 0,
          month6: preCalculated.monthlyDistribution.month6 || 0,
          month7: preCalculated.monthlyDistribution.month7 || 0,
          month8: preCalculated.monthlyDistribution.month8 || 0,
          month9: preCalculated.monthlyDistribution.month9 || 0,
          month10: preCalculated.monthlyDistribution.month10 || 0,
          month11: preCalculated.monthlyDistribution.month11 || 0,
          month12: preCalculated.monthlyDistribution.month12 || 0,
        };
        return { annual: preCalculated.annualTotal, monthly };
      }

      // Fallback: distribute TOTAL_EXPENSES proportionally based on historical data
      const deptTotalExpenses = getDeptTotalExpensesFromCalc(deptName);
      const historicalDeptTotal = getHistoricalDeptExpenseTotal(deptName);
      const historicalAccAmount = getHistoricalAccountExpense(accCode);

      if (historicalDeptTotal > 0 && deptTotalExpenses > 0) {
        // Calculate this account's proportion of historical expenses
        const proportion = historicalAccAmount / historicalDeptTotal;
        accAmount = Math.round(proportion * deptTotalExpenses);
      }
    } else {
      // For regular locations: use expense percentage × gross
      const percent = getProjectionValue(deptName, `EXPENSE_${accCode}`);
      accAmount = percent !== null ? Math.round((percent / 100) * projectedGross) : 0;
    }

    if (accAmount === 0) {
      return { annual: 0, monthly: createEmptyMonthlyTotals() };
    }
    const accWeights = getAccountSeasonalityWeights(accCode);
    // Use account-specific weights if available, otherwise fall back to department weights
    const weights = accWeights.every(w => Math.abs(w - 1/12) < 0.0001) ? deptFallbackWeights : accWeights;
    return { annual: accAmount, monthly: applySeasonality(accAmount, weights) };
  };

  // Calculate monthly values for a category by summing account monthly values (bottom-up)
  const getCategoryMonthlyExpense = (
    category: ExpenseCategory,
    deptName: string,
    projectedGross: number,
    deptFallbackWeights: number[]
  ): { annual: number; monthly: MonthlyTotals; accountMonthly: Map<string, MonthlyTotals> } => {
    let annual = 0;
    let monthly = createEmptyMonthlyTotals();
    const accountMonthly = new Map<string, MonthlyTotals>();

    // Only include accounts that have historical data
    const accountsWithData = category.accounts.filter(acc => {
      return hasAccountData(acc.code);
    });

    accountsWithData.forEach(acc => {
      const result = getAccountMonthlyExpense(acc.code, deptName, projectedGross, deptFallbackWeights);
      annual += result.annual;
      monthly = addMonthlyTotals(monthly, result.monthly);
      accountMonthly.set(acc.code, result.monthly);
    });

    return { annual, monthly, accountMonthly };
  };

  // Get projected units by brand for a department
  const getProjectedUnitsByBrand = (department: string): Record<string, number> => {
    const result: Record<string, number> = {};
    const prefix = `${department}#UNITS_`;

    Object.keys(localInputs).forEach(key => {
      if (key.startsWith(prefix)) {
        const brand = key.replace(prefix, '');
        result[brand] = localInputs[key].value;
      }
    });

    inputs.forEach(input => {
      if (input.department === department && input.metric.startsWith('UNITS_')) {
        const brand = input.metric.replace('UNITS_', '');
        if (result[brand] === undefined) {
          result[brand] = input.value;
        }
      }
    });

    return result;
  };


  return {
    calculationsBaseMap,
    historical2025,
    historical2024,
    historical2023,
    extraDisplayYearKey,
    extraDisplayAccountCodes,
    inputAccountCodes,
    calculationsMap,
    actualDeptDataMap,
    actualAccountMap,
    actualUnitCounts,
    getActualDeptGross,
    getActualDeptExpenses,
    getActualFrontGross,
    getActualFIGross,
    getActualDeptSales,
    getActualDeptProfit,
    getServicePayTypeBreakdown,
    getPartsPayTypeBreakdown,
    getActualAccountValue,
    getStoredMonthlyDistribution,
    getVehicleDeptMonthlyGross,
    grossMap2023,
    grossMap2024,
    grossMap2025,
    expenseMap2023,
    expenseMap2024,
    expenseMap2025,
    accountMap2023,
    accountMap2024,
    accountMap2025,
    hasAccountData,
    getDeptGrossSeasonalityWeights,
    getDeptExpenseSeasonalityWeights,
    getBrandUnitSeasonalityWeights,
    getDeptUnitSeasonalityWeights,
    getAccountSeasonalityWeights,
    getDeptTotalExpensesFromCalc,
    getHistoricalDeptExpenseTotal,
    getHistoricalAccountExpense,
    getAccountMonthlyExpense,
    getCategoryMonthlyExpense,
    getProjectedUnitsByBrand,
  };
}

export type SeasonalityModeData = ReturnType<typeof useSeasonalityModeData>;
